import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContractContext } from "../../../../../providers/contract";
import { ECONTRACT_STATUS } from "../../../../../enums";
import { getAccessTokenSync, getUserId } from "../../../../../setup/auth/oidc";
import {
  EContractType,
  ESenderRole,
  ETrasactionsType,
} from "../../../../../setup/api/services/contract/models";
import { EContractActions } from "../../../../../setup/api/services/contractActions/enum";
import {
  doAction,
  externalDoAction,
  getAdminActorRole,
  getSettlementActorRole,
} from "../../../../../setup/api/services/contractActions";
import { handleCompressImage } from "../../../../../setup/util/helpers";
import { uploadFile } from "../../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../../s3Integration/enums";
import styles from "./styles.module.scss";
import classNames from "classnames";
import Textarea from "../../../../../components/forms/textarea";
import ImageUpload from "../../../../../components/forms/imageUpload";
import { CircularProgress } from "@mui/material";
import PDFComponent from "../../../../../components/common/PDFComponent";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import UserCard from "../../../../cashouts/CashoutCard/UserCard";
import AmountField from "./AmountField";

interface ManualAdvancedSettlementProps {
  propose?: boolean;
  onSuccess?: () => void;
}

const Amount = ({ value }: { value: string }) => (
  <span className={styles.amount}>
    <span>&#xFDFC;</span>
    <span>{value}</span>
  </span>
);

const ManualAdvancedSettlement = ({
  propose,
  onSuccess,
}: ManualAdvancedSettlementProps) => {
  const { t } = useTranslation();
  const { contract, actions } = useContext(ContractContext);

  const userId = getUserId();
  const userToken = getAccessTokenSync();

  const lockerCashOutMap = useMemo<Record<string, number>>(() => {
    if (!contract?.shipment?.isLocker) return {};
    return Object.fromEntries(
      (contract.transactions ?? [])
        .filter((txn) => txn.type === ETrasactionsType.CASH_OUT)
        .map((txn) => [String(txn.beneficiaryId), txn.amount ?? 0])
    );
  }, [contract]);

  // A destination contract (one transferred from a source contract) gets its
  // parties' amounts pre-filled from their contract-assigned amount, so the
  // admin doesn't have to re-enter them — but only when there's no ongoing
  // arbitration, since arbitration settlements require manually-decided amounts.
  const isDestinationSettlement =
    Boolean(contract?.sourceContractId) &&
    contract?.status !== ECONTRACT_STATUS.DELIVERY_ESCALATION &&
    contract?.status !== ECONTRACT_STATUS.INSPECTION_ESCALATION;

  // Create initial form data with CUSTOMER, PROVIDER, and BROKER arrays
  // Each array has the same length as the corresponding array in contract.partiesProfiles
  // Each entry is an object with value, fee, and userId properties
  const initialFormData: {
    CUSTOMER: Array<{
      value: number | undefined;
      userId: number;
    }>;
    PROVIDER: Array<{
      value: number | undefined;
      userId: number;
    }>;
    BROKER: Array<{
      value: number | undefined;
      userId: number;
    }>;
  } = {
    CUSTOMER: contract?.partiesProfiles?.CUSTOMER
      ? contract.partiesProfiles.CUSTOMER.map((profile) => ({
          value:
            lockerCashOutMap[String(profile.userIdentification)] ??
            (isDestinationSettlement ? profile.amount : undefined),
          userId: profile.id || 0,
        }))
      : [],
    PROVIDER: contract?.partiesProfiles?.PROVIDER
      ? contract.partiesProfiles.PROVIDER.map((profile) => ({
          value:
            lockerCashOutMap[String(profile.userIdentification)] ??
            (isDestinationSettlement ? profile.amount : undefined),
          userId: profile.id || 0,
        }))
      : [],
    BROKER: contract?.partiesProfiles?.BROKER
      ? contract.partiesProfiles.BROKER.map((profile) => ({
          value:
            lockerCashOutMap[String(profile.userIdentification)] ??
            (isDestinationSettlement ? profile.amount : undefined),
          userId: profile.id || 0,
        }))
      : [],
  };

  const isClientAdmin = useMemo(() => {
    if (!userToken) return false;
    const base64Url = userToken.split(".")[1]; // Get the payload part of the token
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const data = JSON.parse(jsonPayload);
    return data?.authorities?.some((role) => role === "CLIENT_ADMIN");
  }, [userToken]);

  const [formData, setFormData] = useState(initialFormData);
  const [settled, setSettled] = useState(false);
  const [settleError, setSettleError] = useState(false);
  const [settleErrorMessage, setSettleErrorMessage] = useState("");
  const [description, setDescription] = useState<string>("");
  const [entities, setEntities] = useState<
    {
      file: File;
      s3File: string;
      isLoading: boolean;
      loadingProgress: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);

  // direction signifier: only relevant once money is actually being assigned
  // back to the buyer (CUSTOMER) — otherwise this settlement isn't moving
  // anything toward the source contract, so the badge would be misleading
  const isDestinationContract =
    Boolean(contract?.sourceContractId) &&
    formData.CUSTOMER.some((customer) => (customer.value || 0) > 0);
  const isSourceContract = Boolean(contract?.settlementRequests?.length);

  const maxAmount = useMemo(() => {
    if (!contract) return 0;
    if (contract.type === EContractType.BROKERAGE_CONTRACT)
      return contract.itemPrice;
    return (
      contract.transactions
        ?.filter(
          (t) =>
            (t.type === ETrasactionsType.CUSTOMER_PAY ||
              t.type === ETrasactionsType.MANUAL_BANK_TRANSFER) &&
            t.status === "SUCCESS"
        )
        .reduce((sum, t) => sum + (t.amount || 0), 0) || 0
    );
  }, [contract]);

  const currentTotalValues = useMemo(() => {
    let totalValues = 0;
    formData.BROKER.forEach((broker) => {
      totalValues += broker.value || 0;
    });
    formData.CUSTOMER.forEach((customer) => {
      totalValues += customer.value || 0;
    });
    formData.PROVIDER.forEach((provider) => {
      totalValues += provider.value || 0;
    });
    return totalValues;
  }, [formData]);

  // compare at cent precision (integers) instead of raw floats — money never
  // has more than 2 decimal places here, so this is an exact equality check,
  // not a tolerance window that could mask a real mismatch
  const maxAmountCents = Math.round(maxAmount * 100);
  const currentTotalCents = Math.round(currentTotalValues * 100);
  const isOver = currentTotalCents > maxAmountCents;
  const isBalanced =
    currentTotalCents === maxAmountCents && currentTotalCents > 0;
  const progressPercent =
    maxAmount > 0 ? Math.min(100, (currentTotalValues / maxAmount) * 100) : 0;

  const buttonDisabled = useCallback(() => {
    return loading || !isBalanced;
  }, [loading, isBalanced]);

  const handleSettleContract = async () => {
    if (!contract) return;
    if (!userId) return;
    if (!isBalanced) return;

    setLoading(true);
    setSettleError(false);
    setSettleErrorMessage("");

    const cashOutAmountList: {
      id: string;
      feePercentage?: number;
      amountDue: number;
    }[] = [];

    formData.BROKER.forEach((broker) => {
      if (broker.value !== undefined) {
        const userIdentification = contract.partiesProfiles?.BROKER.find(
          (br) => br.id === broker.userId
        )?.userIdentification;
        cashOutAmountList.push({
          id: isClientAdmin
            ? String(userIdentification)
            : String(broker.userId),
          feePercentage: isClientAdmin ? undefined : 0,
          amountDue: broker.value,
        });
      }
    });
    formData.PROVIDER.forEach((provider) => {
      if (provider.value !== undefined) {
        const userIdentification = contract.partiesProfiles?.PROVIDER.find(
          (pr) => pr.id === provider.userId
        )?.userIdentification;
        cashOutAmountList.push({
          id: isClientAdmin
            ? String(userIdentification)
            : String(provider.userId),
          feePercentage: isClientAdmin ? undefined : 0,
          amountDue: provider.value,
        });
      }
    });
    formData.CUSTOMER.forEach((customer) => {
      if (customer.value !== undefined) {
        const userIdentification = contract.partiesProfiles?.CUSTOMER.find(
          (cu) => cu.id === customer.userId
        )?.userIdentification;
        cashOutAmountList.push({
          id: isClientAdmin
            ? String(userIdentification)
            : String(customer.userId),
          feePercentage: isClientAdmin ? undefined : 0,
          amountDue: customer.value,
        });
      }
    });

    let cashOutAttachments: string[] | undefined = undefined;

    if (entities.length)
      cashOutAttachments = entities.map((entity) => entity?.s3File);

    try {
      if (propose) {
        const proposedSettlement: { id: string; amountDue: number }[] = [];

        formData.PROVIDER.forEach((provider, index) => {
          if (provider.value !== undefined) {
            const userIdentification =
              contract.partiesProfiles?.PROVIDER[index]?.userIdentification;
            if (userIdentification != null)
              proposedSettlement.push({
                id: String(userIdentification),
                amountDue: provider.value,
              });
          }
        });

        formData.CUSTOMER.forEach((customer, index) => {
          if (customer.value !== undefined) {
            const userIdentification =
              contract.partiesProfiles?.CUSTOMER[index]?.userIdentification;
            if (userIdentification != null)
              proposedSettlement.push({
                id: String(userIdentification),
                amountDue: customer.value,
              });
          }
        });

        formData.BROKER.forEach((broker, index) => {
          if (broker.value !== undefined) {
            const userIdentification =
              contract.partiesProfiles?.BROKER[index]?.userIdentification;
            if (userIdentification != null)
              proposedSettlement.push({
                id: String(userIdentification),
                amountDue: broker.value,
              });
          }
        });

        const totalProviderValue = formData.PROVIDER.reduce(
          (sum, p) => sum + (p.value || 0),
          0
        );
        const totalCustomerValue = formData.CUSTOMER.reduce(
          (sum, c) => sum + (c.value || 0),
          0
        );
        const resolution =
          totalProviderValue >= totalCustomerValue
            ? "RESOLVED_PROVIDER"
            : "RESOLVED_CUSTOMER";

        const res = await doAction({
          contractId: contract?.id,
          userId: String(userId),
          actorRole: getAdminActorRole(),
          contractAction: EContractActions.ADMIN_RESOLVE_ESCALATION,
          escalationDTO: {
            resolution,
            proposedSettlement,
          },
        } as any);
        setLoading(false);
        if (res?.sent) {
          setSettled(true);
        } else {
          setSettleError(true);
        }
      } else {
        const body = {
          contractId: contract?.id,
          userId: userId,
          actorRole: isClientAdmin
            ? ESenderRole.CLIENT_ADMIN
            : getSettlementActorRole(),
          senderId: String(contract?.senderId),
          contractAction: isClientAdmin
            ? EContractActions.SETTLE_CONTRACT
            : EContractActions.ADMIN_SETTLE_CONTRACT,
          contractType: isClientAdmin ? contract.type : undefined,
          cashOutAmountList: cashOutAmountList,
          cashOutComment: description || undefined,
          cashOutAttachments,
        };
        const res = isClientAdmin
          ? await externalDoAction(body)
          : await doAction(body);
        setLoading(false);
        if (res?.sent) {
          setSettled(true);
        } else {
          setSettleError(true);
        }
      }
    } catch (error) {
      console.log("error", error);
      setLoading(false);
      setSettleError(true);
      setSettleErrorMessage((error as any)?.error?.message || "");
    }
  };

  const handleDone = () => {
    if (propose) {
      actions.getContract();
      onSuccess?.();
    } else {
      window.location.reload();
    }
  };

  const handleFileUpload = useCallback(
    async (e: any) => {
      const tempEntities: {
        file: File;
        s3File: string;
        isLoading: boolean;
        loadingProgress: number;
      }[] = [...entities];
      const allowedFormats = [
        "image/x-png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/png",
        "application/pdf",
      ];
      try {
        const totalFiles =
          e?.target?.files?.length + entities.length > 10
            ? 10 - entities.length
            : e?.target?.files?.length;

        setEntities((prevEntities) => {
          return [
            ...prevEntities,
            ...Array.from(Array(totalFiles).keys()).map(() => ({
              file: undefined as unknown as File,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            })),
          ];
        });

        for (let i = 0; i < totalFiles; i++) {
          const file = e?.target?.files[i];

          if (allowedFormats.indexOf(file?.type) === -1) {
            return;
          }

          if (file?.type !== "application/pdf") {
            tempEntities.push({
              file,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            });

            const image = await handleCompressImage(file);

            if (image) {
              const entryIndex = tempEntities.findIndex(
                (entity) => entity?.file?.name === image?.name
              );

              tempEntities[entryIndex].file = image;
            }
          } else {
            tempEntities.push({
              file,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            });
          }

          if (i === totalFiles - 1) setEntities(tempEntities);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [entities]
  );

  const uploadFiles = useCallback(async () => {
    const entityArray = [...entities];
    const uploadRequests: Promise<void>[] = [];
    for (let i = 0; i < entityArray?.length; i++) {
      const file = entityArray?.[i]?.file;

      if (
        entityArray?.[i]?.file &&
        entityArray?.[i]?.isLoading &&
        entityArray?.[i]?.loadingProgress === 0 &&
        !entityArray?.[i]?.s3File
      ) {
        const uploadRequest = uploadFile(
          file,
          EUploadImageType.CONTRACT,
          "TEMP_WAFFY_SYSTEM",
          (progress) =>
            setEntities((prevEntities) => {
              const newEntities = prevEntities?.map((entity) => {
                if (entity.file.name === file.name) {
                  return {
                    ...entity,
                    loadingProgress: Math.round(
                      (progress?.loaded / progress?.total) * 100
                    ),
                  };
                }
                return entity;
              });
              return newEntities;
            })
        ).then((url) => {
          setEntities((prevEntities) => {
            const newEntities = prevEntities?.map((entity) => {
              if (entity.file.name === file.name) {
                return {
                  file,
                  s3File: url,
                  isLoading: false,
                  loadingProgress: 0,
                };
              }

              return entity;
            });
            return newEntities;
          });
        });

        uploadRequests.push(uploadRequest);
      }
    }

    await Promise.all(uploadRequests);
  }, [entities]);

  useEffect(() => {
    if (
      entities.length > 0 &&
      entities.some(
        (entity) => entity?.isLoading && entity.loadingProgress === 0
      )
    )
      uploadFiles();
  }, [entities, uploadFiles]);

  const handleDeleteFile = useCallback(
    (index: number) => {
      const tempEntities = [...entities];
      tempEntities.splice(index, 1);
      setEntities(tempEntities);
    },
    [entities]
  );

  if (settled) {
    return (
      <div className={styles.container}>
        <div className={styles.success}>
          <span className={styles.ok_badge}>
            <img src="/assets/images/svg/check_success.svg" alt="" />
          </span>
          <p className={styles.success_title}>
            {t("lbl_settlement_success_title")}
          </p>
          <p className={styles.success_subtitle}>
            {t("lbl_settlement_success_subtitle")}
          </p>
          <PrimaryButton
            style={{ width: "auto", minWidth: "220px", height: "48px" }}
            onClick={handleDone}>
            {t("lbl_back_to_transaction_details")}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {(isDestinationContract || isSourceContract) && (
          <div className={styles.direction_badge}>
            <img src="/assets/images/svg/transfer_direction.svg" alt="" />
            <span>
              {isDestinationContract
                ? t("lbl_destination_settlement_badge")
                : t("lbl_source_settlement_badge")}
            </span>
          </div>
        )}

        <div className={styles.sum_card}>
          <div className={styles.sum_top}>
            <div>
              <p className={styles.sum_label}>{t("lbl_remaining_amount")}</p>
              <p className={styles.sum_value}>
                <Amount value={maxAmount.toFixed(2)} />
              </p>
            </div>
            <div className={styles.sum_dist}>
              <p className={styles.k}>{t("lbl_settled_amount")}</p>
              <p className={styles.v}>
                <Amount value={currentTotalValues.toFixed(2)} />
              </p>
            </div>
          </div>
          <div className={styles.bar}>
            <div
              className={classNames(styles.bar_fill, {
                [styles.over]: isOver,
                [styles.balanced]: isBalanced,
              })}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p
            className={classNames(styles.sum_status, {
              [styles.over]: isOver,
              [styles.balanced]: isBalanced,
            })}>
            {isBalanced && (
              <img src="/assets/images/svg/check_success.svg" alt="" />
            )}
            {isOver && (
              <img src="/assets/images/svg/alert_circle_error.svg" alt="" />
            )}
            {isOver ? (
              <span>
                {t("lbl_exceeds_remaining_by")}{" "}
                <Amount value={(currentTotalValues - maxAmount).toFixed(2)} />
              </span>
            ) : isBalanced ? (
              <span>{t("lbl_fully_distributed")}</span>
            ) : (
              <span>
                {t("lbl_undistributed_remaining")}:{" "}
                <Amount
                  value={Math.max(0, maxAmount - currentTotalValues).toFixed(2)}
                />
              </span>
            )}
          </p>
        </div>

        {isOver && (
          <p className={styles.alert_pill}>
            <span className={styles.bang}>!</span>
            <span>{t("lbl_distribution_exceeds_alert")}</span>
          </p>
        )}

        {settleError && (
          <p className={styles.alert_pill}>
            <span className={styles.bang}>!</span>
            <span>
              {settleErrorMessage || t("lbl_settlement_failed_alert")}
            </span>
          </p>
        )}

        {formData.CUSTOMER.length > 0 && (
          <div className={styles.sec}>
            <div className={styles.sec_head}>
              <h3 className={styles.sec_title}>{t("lbl_payees")}</h3>
              <span className={styles.sec_count}>
                {formData.CUSTOMER.length}
              </span>
            </div>
            {formData.CUSTOMER.map((customer, index) => {
              const customerUid = String(
                contract?.partiesProfiles?.CUSTOMER?.[index]?.userIdentification
              );
              const isLockerLocked = customerUid in lockerCashOutMap;
              return (
                <div key={`customer-${index}`} className={styles.p_card}>
                  <UserCard
                    name={
                      contract?.partiesProfiles?.CUSTOMER?.[index]?.profile
                        ?.fullName || ""
                    }
                    type={
                      contract?.partiesProfiles?.CUSTOMER?.[index]
                        ?.phoneNumber || ""
                    }
                    image={
                      contract?.partiesProfiles?.CUSTOMER?.[index]?.profile
                        ?.photoUrl || ""
                    }
                    isNationalIdVerified={
                      contract?.partiesProfiles?.CUSTOMER?.[index]
                        ?.nationalIdVerified
                    }
                    isDescriptionLtr
                  />
                  <AmountField
                    value={customer.value}
                    disabled={isLockerLocked}
                    maxValue={maxAmount}
                    onChange={(value) => {
                      const newFormData = { ...formData };
                      newFormData.CUSTOMER[index].value = value;
                      setFormData(newFormData);
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {formData.PROVIDER.length > 0 && (
          <div className={styles.sec}>
            <div className={styles.sec_head}>
              <h3 className={styles.sec_title}>{t("lbl_receivers")}</h3>
              <span className={styles.sec_count}>
                {formData.PROVIDER.length}
              </span>
            </div>
            {formData.PROVIDER.map((provider, index) => {
              const providerUid = String(
                contract?.partiesProfiles?.PROVIDER?.[index]?.userIdentification
              );
              const isLockerLocked = providerUid in lockerCashOutMap;
              return (
                <div key={`provider-${index}`} className={styles.p_card}>
                  <UserCard
                    name={
                      contract?.partiesProfiles?.PROVIDER?.[index]?.profile
                        ?.fullName || ""
                    }
                    type={
                      contract?.partiesProfiles?.PROVIDER?.[index]
                        ?.phoneNumber || ""
                    }
                    image={
                      contract?.partiesProfiles?.PROVIDER?.[index]?.profile
                        ?.photoUrl || ""
                    }
                    isNationalIdVerified={
                      contract?.partiesProfiles?.PROVIDER?.[index]
                        ?.nationalIdVerified
                    }
                    isDescriptionLtr
                  />
                  <AmountField
                    value={provider.value}
                    disabled={isLockerLocked}
                    maxValue={maxAmount}
                    onChange={(value) => {
                      const newFormData = { ...formData };
                      newFormData.PROVIDER[index].value = value;
                      setFormData(newFormData);
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {formData.BROKER.length > 0 && (
          <div className={styles.sec}>
            <div className={styles.sec_head}>
              <h3 className={styles.sec_title}>{t("lbl_brokers")}</h3>
              <span className={styles.sec_count}>{formData.BROKER.length}</span>
            </div>
            {formData.BROKER.map((broker, index) => {
              const brokerUid = String(
                contract?.partiesProfiles?.BROKER?.[index]?.userIdentification
              );
              const isLockerLocked = brokerUid in lockerCashOutMap;
              return (
                <div key={`broker-${index}`} className={styles.p_card}>
                  <UserCard
                    name={
                      contract?.partiesProfiles?.BROKER?.[index]?.profile
                        ?.fullName || ""
                    }
                    type={
                      contract?.partiesProfiles?.BROKER?.[index]?.phoneNumber ||
                      ""
                    }
                    image={
                      contract?.partiesProfiles?.BROKER?.[index]?.profile
                        ?.photoUrl || ""
                    }
                    isNationalIdVerified={
                      contract?.partiesProfiles?.BROKER?.[index]
                        ?.nationalIdVerified
                    }
                    isDescriptionLtr
                  />
                  <AmountField
                    value={broker.value}
                    disabled={isLockerLocked}
                    maxValue={maxAmount}
                    onChange={(value) => {
                      const newFormData = { ...formData };
                      newFormData.BROKER[index].value = value;
                      setFormData(newFormData);
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.horizontal_line}></div>
        <div className={styles.info_container}>
          <h2 className={styles.info_title}>
            {t("lbl_contract_details_notes")}
          </h2>
          <Textarea
            label={t("lbl_add_description")}
            required={false}
            value={description}
            maxCharacters={500}
            maxInputHeight={"170px"}
            handleChange={(e) => setDescription(e.target.value)}
            minInputHeight="100px"
          />
          <div className={styles["form-scroll"]}>
            {entities?.map((entity) => entity?.file)?.length < 10 && (
              <div key={0} style={{ marginBottom: "0.2rem" }}>
                <ImageUpload withPDF multiple handleChange={handleFileUpload} />
              </div>
            )}
            {entities?.map((entity, index) => (
              <div key={index} className={styles["uploaded_image_container"]}>
                {entity.isLoading && entity.loadingProgress >= 0 ? (
                  <div
                    className={classNames(
                      styles["uploaded_image"],
                      styles["upload_skeleton"]
                    )}>
                    <CircularProgress
                      {...(!entity?.loadingProgress
                        ? {}
                        : {
                            variant: "determinate",
                            value: entity?.loadingProgress,
                          })}
                    />
                  </div>
                ) : (
                  <>
                    {entity?.file?.type === "application/pdf" ? (
                      <PDFComponent
                        fileUrl={entity?.s3File}
                        fileName={entity?.file?.name}
                        isPreview
                      />
                    ) : (
                      <img
                        className={styles["uploaded_image"]}
                        src={entity?.s3File}
                      />
                    )}
                  </>
                )}
                {entity?.s3File && (
                  <div
                    onClick={() => handleDeleteFile(index)}
                    className={styles["delete_icon"]}>
                    <img src="/assets/images/svg/bin.svg" alt="bin" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.cta_container}>
        <PrimaryButton
          style={{
            backgroundColor: buttonDisabled() ? "#9FA6AB" : "#0D44AD",
            height: "48px",
            color: "#FFFFFF",
            cursor: buttonDisabled() ? "not-allowed" : "pointer",
            fontSize: "1rem",
            fontWeight: 600,
          }}
          onClick={handleSettleContract}
          disabled={buttonDisabled()}>
          {loading ? (
            <CircularProgress size={18} style={{ color: "#fff" }} />
          ) : propose ? (
            t("lbl_propose_settlement")
          ) : (
            t("lbl_confirm_settlement")
          )}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default ManualAdvancedSettlement;
